import { moneyFromMajor, type LocalDate } from '@expense-assistant/finance-core';

export interface AppAccount {
  id: string;
  name: string;
  type: string;
  balance: number;
  spendable: boolean;
  protected: boolean;
  minimumBalance: number;
}

export interface AppTransaction {
  id: string;
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER';
  label: string;
  amount: number;
  date: LocalDate;
  accountId: string;
  toAccountId?: string;
  category: string;
  group: string;
  notes?: string;
  plannedEventId?: string;
  status: 'COMPLETED' | 'PLANNED';
}

export interface AppPlan {
  id: string;
  label: string;
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER';
  amount: number;
  originalAmount?: number;
  date: LocalDate;
  accountId: string;
  toAccountId?: string;
  group: string;
  mandatory: boolean;
  status: 'UPCOMING' | 'DUE' | 'OVERDUE' | 'PARTIALLY_SETTLED' | 'SETTLED';
}

export interface AppGoal {
  id: string;
  name: string;
  target: number;
  allocated: number;
  targetDate: LocalDate;
  monthlyContribution: number;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED';
}

export interface AppDebt {
  id: string;
  counterparty: string;
  direction: 'OWED_TO_ME' | 'I_OWE';
  originalAmount: number;
  remainingAmount: number;
  dueDate: LocalDate;
  status: 'OPEN' | 'PARTIAL' | 'SETTLED' | 'OVERDUE';
}

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  read: boolean;
  timestamp: string;
}

export interface AppCategory {
  id: string;
  name: string;
  applicability: 'INCOME' | 'EXPENSE' | 'BOTH';
  active: boolean;
}

export interface AppBudgetGroup {
  id: string;
  name: string;
  active: boolean;
}

export interface AppData {
  accounts: AppAccount[];
  transactions: AppTransaction[];
  plans: AppPlan[];
  goals: AppGoal[];
  debts: AppDebt[];
  notifications: AppNotification[];
  categories: AppCategory[];
  budgetGroups: AppBudgetGroup[];
  safetyBuffer: number;
}

const lkr = moneyFromMajor;
const currentYearMonth = '2026-09';

export const demoData: AppData = {
  accounts: [
    {
      id: 'salary-account',
      name: 'Salary account',
      type: 'Bank',
      balance: lkr(196_300),
      spendable: true,
      protected: false,
      minimumBalance: lkr(20_000),
    },
    {
      id: 'savings-account',
      name: 'Emergency reserve',
      type: 'Savings',
      balance: lkr(150_000),
      spendable: true,
      protected: true,
      minimumBalance: lkr(150_000),
    },
    {
      id: 'cash-account',
      name: 'Cash',
      type: 'Cash',
      balance: lkr(12_500),
      spendable: true,
      protected: false,
      minimumBalance: 0,
    },
  ],
  transactions: [
    {
      id: 't1',
      type: 'INCOME',
      label: 'My 2nd Salary',
      amount: lkr(168_264),
      date: `${currentYearMonth}-10`,
      accountId: 'salary-account',
      category: 'Salary',
      group: 'Our Home',
      status: 'COMPLETED',
    },
    {
      id: 't2',
      type: 'EXPENSE',
      label: 'Home Rental',
      amount: lkr(35_000),
      date: `${currentYearMonth}-05`,
      accountId: 'salary-account',
      category: 'Housing',
      group: 'Our Home',
      status: 'COMPLETED',
    },
    {
      id: 't3',
      type: 'EXPENSE',
      label: 'Monthly Groceries',
      amount: lkr(25_000),
      date: `${currentYearMonth}-06`,
      accountId: 'salary-account',
      category: 'Groceries',
      group: 'Mom’s Home',
      status: 'COMPLETED',
    },
  ],
  plans: [
    {
      id: 'p-income-1',
      label: 'My 1st Salary',
      type: 'INCOME',
      amount: lkr(267_500),
      date: `${currentYearMonth}-25`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-income-2',
      label: 'My 2nd Salary',
      type: 'INCOME',
      amount: lkr(168_264),
      date: `${currentYearMonth}-10`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-home-rent',
      label: 'Home Rental',
      type: 'EXPENSE',
      amount: lkr(35_000),
      date: `${currentYearMonth}-07`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-loan',
      label: 'Loan',
      type: 'EXPENSE',
      amount: lkr(60_000),
      date: `${currentYearMonth}-28`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-lease',
      label: 'Vehicle Lease',
      type: 'EXPENSE',
      amount: lkr(112_200),
      date: `${currentYearMonth}-28`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-seetuwa',
      label: 'Seetuwa (Gonaduwa)',
      type: 'EXPENSE',
      amount: lkr(10_000),
      date: `${currentYearMonth}-28`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-our-groceries',
      label: 'Monthly Groceries',
      type: 'EXPENSE',
      amount: lkr(25_000),
      date: `${currentYearMonth}-07`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-electricity',
      label: 'Electricity Bill',
      type: 'EXPENSE',
      amount: lkr(7_811),
      date: `${currentYearMonth}-30`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-internet',
      label: 'Internet Bill',
      type: 'EXPENSE',
      amount: lkr(5_000),
      date: `${currentYearMonth}-20`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'OVERDUE',
    },
    {
      id: 'p-water',
      label: 'Water Bill',
      type: 'EXPENSE',
      amount: lkr(3_000),
      date: `${currentYearMonth}-30`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-mobile',
      label: 'Mobile Bill',
      type: 'EXPENSE',
      amount: lkr(3_000),
      date: `${currentYearMonth}-30`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-sampath-card',
      label: 'Sampath Credit Card',
      type: 'EXPENSE',
      amount: lkr(5_000),
      date: `${currentYearMonth}-30`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-commercial-card',
      label: 'Commercial Credit Card',
      type: 'EXPENSE',
      amount: lkr(5_000),
      date: `${currentYearMonth}-30`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: true,
      status: 'UPCOMING',
    },
    {
      id: 'p-fuel',
      label: 'Fuel Expenses',
      type: 'EXPENSE',
      amount: lkr(25_000),
      date: `${currentYearMonth}-30`,
      accountId: 'salary-account',
      group: 'Our Home',
      mandatory: false,
      status: 'UPCOMING',
    },
    {
      id: 'p-mom-groceries',
      label: 'Monthly Groceries',
      type: 'EXPENSE',
      amount: lkr(25_000),
      date: `${currentYearMonth}-07`,
      accountId: 'salary-account',
      group: 'Mom’s Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-mom-electricity',
      label: 'Electricity Bill',
      type: 'EXPENSE',
      amount: lkr(4_000),
      date: `${currentYearMonth}-07`,
      accountId: 'salary-account',
      group: 'Mom’s Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-mom-telecom',
      label: 'Telecom Bill',
      type: 'EXPENSE',
      amount: lkr(2_500),
      date: `${currentYearMonth}-07`,
      accountId: 'salary-account',
      group: 'Mom’s Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-mom-water',
      label: 'Water Bill',
      type: 'EXPENSE',
      amount: lkr(2_000),
      date: `${currentYearMonth}-07`,
      accountId: 'salary-account',
      group: 'Mom’s Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-mom-medicines',
      label: 'Monthly Medicines',
      type: 'EXPENSE',
      amount: lkr(15_000),
      date: `${currentYearMonth}-07`,
      accountId: 'salary-account',
      group: 'Mom’s Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-mom-interest',
      label: 'Jewellery Interest',
      type: 'EXPENSE',
      amount: lkr(15_000),
      date: `${currentYearMonth}-07`,
      accountId: 'salary-account',
      group: 'Mom’s Home',
      mandatory: true,
      status: 'SETTLED',
    },
    {
      id: 'p-mom-emergency',
      label: 'Emergency Fund for Mom & Dad',
      type: 'EXPENSE',
      amount: lkr(5_000),
      date: `${currentYearMonth}-30`,
      accountId: 'salary-account',
      group: 'Mom’s Home',
      mandatory: false,
      status: 'UPCOMING',
    },
    {
      id: 'p-inlaw-overall',
      label: 'Overall Expenses',
      type: 'EXPENSE',
      amount: lkr(10_000),
      date: `${currentYearMonth}-30`,
      accountId: 'salary-account',
      group: 'Mom-in-law’s Home',
      mandatory: false,
      status: 'UPCOMING',
    },
  ],
  goals: [
    {
      id: 'g1',
      name: 'Emergency Fund',
      target: lkr(600_000),
      allocated: lkr(150_000),
      targetDate: '2027-08-31',
      monthlyContribution: lkr(40_000),
      priority: 'HIGH',
      status: 'ACTIVE',
    },
    {
      id: 'g2',
      name: 'Dream Vacation',
      target: lkr(450_000),
      allocated: lkr(72_500),
      targetDate: '2027-12-31',
      monthlyContribution: lkr(20_000),
      priority: 'MEDIUM',
      status: 'ACTIVE',
    },
  ],
  debts: [
    {
      id: 'd1',
      counterparty: 'Nimal',
      direction: 'OWED_TO_ME',
      originalAmount: lkr(50_000),
      remainingAmount: lkr(30_000),
      dueDate: '2026-10-15',
      status: 'PARTIAL',
    },
    {
      id: 'd2',
      counterparty: 'Family loan',
      direction: 'I_OWE',
      originalAmount: lkr(120_000),
      remainingAmount: lkr(120_000),
      dueDate: '2026-11-30',
      status: 'OPEN',
    },
  ],
  notifications: [
    {
      id: 'n1',
      title: 'Internet bill is overdue',
      message: 'LKR 5,000 was due before today and is not marked paid.',
      severity: 'WARNING',
      read: false,
      timestamp: '2026-09-21T08:30:00+05:30',
    },
    {
      id: 'n2',
      title: 'Salary expected in 4 days',
      message: 'My 1st Salary of LKR 267,500 is expected on 25 September.',
      severity: 'INFO',
      read: false,
      timestamp: '2026-09-21T08:30:00+05:30',
    },
    {
      id: 'n3',
      title: 'Backup reminder',
      message: 'Download a complete financial backup and store it safely off this device.',
      severity: 'INFO',
      read: true,
      timestamp: '2026-09-20T09:00:00+05:30',
    },
  ],
  categories: [
    'Salary',
    'Housing',
    'Groceries',
    'Utilities',
    'Internet/Telecom',
    'Transport/Fuel',
    'Vehicle',
    'Loan',
    'Credit Card',
    'Medical',
    'Interest',
    'Family Support',
    'Savings',
    'Emergency Fund',
    'Recurring Commitment',
    'Entertainment',
    'Shopping',
    'Other',
  ].map((name) => ({
    id: `category-${name.toLowerCase().replaceAll(/[^a-z]+/g, '-')}`,
    name,
    applicability:
      name === 'Salary'
        ? 'INCOME'
        : name === 'Interest' || name === 'Savings' || name === 'Emergency Fund' || name === 'Other'
          ? 'BOTH'
          : 'EXPENSE',
    active: true,
  })),
  budgetGroups: ['Our Home', 'Mom’s Home', 'Mom-in-law’s Home'].map((name) => ({
    id: `group-${name.toLowerCase().replaceAll(/[^a-z]+/g, '-')}`,
    name,
    active: true,
  })),
  safetyBuffer: lkr(25_000),
};
